import { useState } from 'react'
import { useAuth } from './contexts/AuthContext'
import Sidebar from './components/Sidebar'
import SetupWizard from './components/SetupWizard'
import MatchingInterface from './components/MatchingInterface'
import WidgetPreview from './components/WidgetPreview'
import Dashboard from './components/Dashboard'
import Settings from './components/Settings'
import UserProfile from './components/UserProfile'

// ── Trial banner (soft prompt — days 1–10) ─────────────────────────────────
function TrialBanner({ daysLeft, onDismiss }) {
  const isUrgent = daysLeft <= 3

  return (
    <div className={`flex items-center justify-between px-5 py-2.5 text-sm flex-shrink-0 ${
      daysLeft === 1
        ? 'bg-red-600 text-white'
        : isUrgent
        ? 'bg-orange-500 text-white'
        : 'bg-amber-50 border-b border-amber-200 text-amber-900'
    }`}>
      <div className="flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        {isUrgent ? (
          <span>
            <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''} left</strong> in your free trial — activate your plan to keep access after it ends.
          </span>
        ) : (
          <span>
            Your free trial ends in <strong>{daysLeft} days</strong>. Activate your plan now to continue without interruption.
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
        <button className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 ${
          isUrgent
            ? 'bg-white text-orange-600 hover:bg-orange-50'
            : 'bg-amber-600 text-white hover:bg-amber-700'
        }`}>
          Activate your plan
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
        </button>
        {!isUrgent && (
          <button onClick={onDismiss} className="text-amber-500 hover:text-amber-700 transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        )}
      </div>
    </div>
  )
}

// ── Payment failed banner (grace period — paying customer) ─────────────────
function PaymentFailedBanner({ graceDaysLeft }) {
  const isUrgent = graceDaysLeft <= 1
  return (
    <div className={`flex items-center justify-between px-5 py-2.5 text-sm flex-shrink-0 ${
      isUrgent ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'
    }`}>
      <div className="flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
        {isUrgent ? (
          <span><strong>Final notice</strong> · Payment still failing. Your account suspends today. Update your card now to keep access.</span>
        ) : (
          <span><strong>Payment failed</strong> · Card ending in ••4242 was declined. Update your payment method — access suspends in {graceDaysLeft} days.</span>
        )}
      </div>
      <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white text-orange-600 hover:bg-orange-50 transition-colors flex items-center gap-1 flex-shrink-0 ml-4">
        Update payment method
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
      </button>
    </div>
  )
}

// ── Suspended gate (grace period exhausted — paying customer) ──────────────
function SuspendedGate() {
  return (
    <div className="flex-1 flex items-center justify-center bg-gray-50 p-8 overflow-y-auto">
      <div className="max-w-sm w-full space-y-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <div>
          <h2 className="text-xl font-bold text-gray-900">Your subscription is paused</h2>
          <p className="text-sm text-gray-500 mt-1.5">
            We couldn't process your last payment. Update your payment method to restore access immediately.
          </p>
        </div>

        <div className="bg-amber-50 rounded-xl px-4 py-3 border border-amber-100 flex items-start gap-2.5 text-left">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <p className="text-xs text-amber-800">Your data is safe. Your account will be fully restored the moment payment clears.</p>
        </div>

        <div className="space-y-3">
          <button className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors text-sm flex items-center justify-center gap-2">
            Update payment method
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </button>
          <button className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Talk to us →</button>
        </div>
      </div>
    </div>
  )
}

// ── Upgrade gate (trial expired) ───────────────────────────────────────────
function UpgradeGate() {
  const features = [
    'Stripe ↔ Salesforce account matching',
    'AI-powered auto-matching',
    'Sales rep widget inside Salesforce',
    'Admin approval & linking workflow',
  ]
  return (
    <div className="flex-1 flex items-center justify-center bg-gray-50 p-8 overflow-y-auto">
      <div className="max-w-sm w-full space-y-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto shadow-sm">
          <span className="text-white text-lg font-bold tracking-tight">BJ</span>
        </div>

        <div>
          <h2 className="text-xl font-bold text-gray-900">Your trial has ended</h2>
          <p className="text-sm text-gray-500 mt-1.5">Set up billing on our portal to keep using BillJean.</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 text-left divide-y divide-gray-100 overflow-hidden">
          {features.map(f => (
            <div key={f} className="flex items-center gap-3 px-4 py-3">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
              <span className="text-sm text-gray-700">{f}</span>
            </div>
          ))}
        </div>

        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <div className="text-2xl font-bold text-gray-900">
            $9 <span className="text-sm font-normal text-gray-500">/ agent / month</span>
          </div>
          <div className="text-xs text-gray-400 mt-1">Billed monthly · Cancel anytime</div>
        </div>

        <div className="space-y-3">
          <button className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors text-sm flex items-center justify-center gap-2">
            Go to billing portal
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </button>
          <button className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
            Talk to us →
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Header Component ────────────────────────────────────────────────────────
function Header({ onNavigate }) {
  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0 z-30 shadow-sm">
      <div className="flex items-center gap-4">
        {/* Breadcrumb or page title can go here */}
      </div>
      <div className="flex items-center gap-4">
        {/* Notifications icon (optional) */}
        <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          {/* Notification badge */}
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        </button>
        {/* User Profile */}
        <UserProfile onNavigate={onNavigate} />
      </div>
    </header>
  )
}

// ── App ────────────────────────────────────────────────────────────────────
export default function App() {
  const { operator } = useAuth()
  const [currentPage, setCurrentPage] = useState('dashboard')
  const [trialDaysLeft, setTrialDaysLeft] = useState(10)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [billingState, setBillingState] = useState('ok') // 'ok' | 'grace-5' | 'grace-1' | 'suspended'

  const showTrialBanner = trialDaysLeft > 0 && trialDaysLeft <= 10 && !bannerDismissed && billingState === 'ok'
  const trialExpired = trialDaysLeft <= 0 && billingState === 'ok'
  const isSuspended = billingState === 'suspended'
  const graceDaysLeft = billingState === 'grace-5' ? 5 : 1

  const handleSetTrialDays = (d) => {
    setTrialDaysLeft(d)
    setBannerDismissed(false)
    setBillingState('ok')  // trial states imply no billing issue
  }

  const handleSetBillingState = (s) => {
    setBillingState(s)
    setBannerDismissed(false)
    if (s !== 'ok') setTrialDaysLeft(30)  // paying customer — clear trial state
  }

  // If not authenticated, redirect will be handled by routing
  if (!operator) {
    return null
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden font-sans">
      <Sidebar
        currentPage={currentPage}
        onNavigate={setCurrentPage}
        trialDaysLeft={trialDaysLeft}
        onSetTrialDays={handleSetTrialDays}
        billingState={billingState}
        onSetBillingState={handleSetBillingState}
      />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header with User Profile */}
        <Header onNavigate={setCurrentPage} />
        {showTrialBanner && (
          <TrialBanner
            daysLeft={trialDaysLeft}
            onDismiss={() => setBannerDismissed(true)}
          />
        )}
        {(billingState === 'grace-5' || billingState === 'grace-1') && (
          <PaymentFailedBanner graceDaysLeft={graceDaysLeft} />
        )}
        {isSuspended ? (
          <SuspendedGate />
        ) : trialExpired ? (
          <UpgradeGate />
        ) : (
          <main className="flex-1 overflow-y-auto min-w-0">
            {currentPage === 'dashboard' && <Dashboard onNavigate={setCurrentPage} />}
            {currentPage === 'matching'  && <MatchingInterface />}
            {currentPage === 'widget'    && <WidgetPreview />}
            {currentPage === 'settings'  && <Settings />}
            {currentPage === 'wizard'    && <SetupWizard onNavigate={setCurrentPage} />}
          </main>
        )}
      </div>
    </div>
  )
}
